/**
 * Key an array of objects by a specified ID.
 *
 * @param array - The array of objects.
 * @param attribute - The key to index the objects by.
 * @returns An object keyed by the specified attribute.
 */
export const keyBy = <T extends Record<string, any>>(
  array: T[],
  attribute: keyof T
): Record<string, T> => {
  const result: Record<string, T> = {}
  array.forEach(item => {
    result[String(item[attribute])] = item
  })
  return result
}

/**
 * Group an array of objects by a specified ID.
 *
 * @param array - The array of objects.
 * @param attribute - The key to group the objects by.
 * @returns An object with arrays of grouped items.
 */
export const groupBy = <T extends Record<string, any>>(
  array: T[],
  attribute: keyof T
): Record<string, T[]> => {
  const result: Record<string, T[]> = {}
  array.forEach(item => {
    const attributeVal = String(item[attribute])
    if (!result.hasOwnProperty(attributeVal)) {
      result[attributeVal] = []
    }
    result[attributeVal].push(item)
  })
  return result
}

/**
 * Filter out duplicate elements of an array.
 *
 * @param element - The current element.
 * @param pos - The index of the element.
 * @param array - The array being filtered.
 * @returns True if the element is unique, otherwise false.
 */
export const isUnique = <T>(element: T, pos: number, array: T[]): boolean => {
  return array.indexOf(element) === pos
}

/**
 * Sum an array of numbers.
 *
 * @param arr - The array of numbers.
 * @returns The sum of the numbers.
 */
export const sum = (arr: number[]): number => arr.reduce((acc, num) => acc + num, 0)

/**
 * Sum an array of objects adding together the values of the specified property.
 *
 * @param array - The array of objects.
 * @param propertyName - The key of the property to sum.
 * @returns The sum of the specified property values.
 */
export const sumProperty = <T extends Record<string, any>>(
  array: T[],
  propertyName: keyof T
): number => {
  return sum(array.map(item => Number(item[propertyName])))
}

export default {
  keyBy,
  groupBy,
  isUnique,
  sum,
  sumProperty
}
